import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'

import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import Clickable from 'components/Common/Clickable'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PayLaterPill from 'components/Common/PayLaterPill'
import type { PriceDetailsModalOfferProps } from 'components/Common/PriceDetails/PriceDetailsModal'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowMarginText from 'components/OfferPage/PriceRowMarginText'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import {
  generateLoyaltyPointsCalculatorOptInBundleOptions,
  generateLoyaltyPointsCalculatorPropertyBundledFlightOptions,
  generateLoyaltyPointsCalculatorPropertyOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { resolveSelectedOptionalBundles } from './propertyMultiRoomOptionalBundles'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { getAgentHubBedbankPackagesForProperty, getPropertyOfferMapping } from 'selectors/offerPage/offerPageSelectors'

interface Props {
  property: App.Property.Property
  isPropertyUltraLux: boolean
  rate: App.EnrichedPropertyRate
  originAirport: App.Airport | undefined
  duration: number
  /** The occupancy room this price belongs to. */
  occupancyRoom: App.Occupants
  checkIn: string | undefined
  checkOut: string | undefined
  agentHubBedbankPackages: Array<{ packageId: string; mx: string }>
  regionCode: string
  canShowMargin: boolean
  ctas?: React.ReactNode
  /**
   * Total of the opt-in bundles selected against this rate. Added to the displayed price and the price
   * details breakdown at render time only -- `rate.totals` stays hotel-only, because margin, GTM and
   * agent commission all read it directly. Points earn is the exception: it takes this as its own
   * `experience`-typed request rather than an inflated room price, since the two earn at different rates.
   */
  optInBundlesPrice?: number
  optInBundleIds?: Array<string>
}

function PropertyRatePrice(props: Props) {
  const {
    property,
    isPropertyUltraLux,
    rate,
    originAirport,
    duration,
    occupancyRoom,
    checkIn,
    checkOut,
    agentHubBedbankPackages,
    regionCode,
    canShowMargin,
    ctas,
    optInBundlesPrice = 0,
    optInBundleIds,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)
  // The commission row takes a room list, but this price only ever covers one occupancy room.
  const occupancyRooms = useMemo(() => [occupancyRoom], [occupancyRoom])

  const rawBedbankDiscount = useBedbankDiscount(rate.totals.discount, rate.luxPlusTotals?.discount ?? 0)

  // Selected opt-in bundles are charged on top of the hotel rate, so only the headline prices move --
  // "valued up to" and the discount figures still describe the hotel rate on its own. A rate with no
  // member price keeps its 0, which is what `LuxPlusPriceGuard` reads as "no member price to show".
  const memberDisplayPrice = rate.luxPlusTotals?.displayPrice ?? 0
  const displayPrice = rate.totals.displayPrice + optInBundlesPrice
  const memberDisplayPriceWithBundles = memberDisplayPrice ? memberDisplayPrice + optInBundlesPrice : 0
  // TODO(HP-1404): Show margin details for LuxPlus+ prices.
  const shouldShowMargin = !!(canShowMargin && rate.totals.margin && rate.totals.marginAud)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      generateLoyaltyPointsCalculatorPropertyOptions(rate, {
        propertyCountryCode: property.location.countryCode,
        propertyStateProvince: property.location.region,
        currentRegionCode: regionCode,
        isUltraLux: isPropertyUltraLux,
      }),
      generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(rate),
      generateLoyaltyPointsCalculatorOptInBundleOptions(resolveSelectedOptionalBundles(rate, optInBundleIds)),
    ]
  }, [rate, property.location.countryCode, property.location.region, regionCode, isPropertyUltraLux, optInBundleIds])

  const openPriceDetailsModal = useCallback(async() => {
    const priceDetailsModalOfferProps: PriceDetailsModalOfferProps = rate.service.type === 'lpc' ? {
      offerId: rate.service.offerId,
      offerType: OFFER_TYPE_ALWAYS_ON,
      saleUnit: 'room',
      displayPricingAsPerPerson: false,
    } : {
      offerId: rate.service.propertyId,
      offerType: OFFER_TYPE_BED_BANK,
      saleUnit: 'room',
      // TODO(HP-1614): Handle `bedbankOffer.displayPricingAsPerPerson`.
      displayPricingAsPerPerson: false,
    }
    const dueAtPropertyMessage = rate.service.type === 'lpc' ? property.taxesAndFeesContent : undefined
    // NB: This is used to narrow down the correct Agent Hub commission. However, it's unclear why
    // we don't do the same with package IDs for LPC rates.
    const rateId = rate.service.type === 'bedbank' ? rate.service.rateId : undefined

    // This implementation is based off `<OfferPriceDetailsRow/>`.
    // NB: Don't pass `flightPrice` to `<PriceDetailsModal/>`, as it's already baked in.
    const result = await showModal<'fine-print' | undefined>(<PriceDetailsModal
      {...priceDetailsModalOfferProps}
      offerPage
      duration={duration}
      taxesAndFees={rate.totals.taxesAndFees}
      propertyFees={rate.totals.propertyFees}
      hotelPrice={rate.totals.sellingPrice}
      hotelMemberPrice={rate.luxPlusTotals?.sellingPrice}
      showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
      rooms={occupancyRooms}
      dueAtPropertyMessage={dueAtPropertyMessage}
      optInBundlesPrice={optInBundlesPrice}
      cta={
        <Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
          {ctas}
        </Group>
      }
      rateId={rateId}
    />)
    if (result === 'fine-print') {
      window.location.href = '#fine-print'
    }
  }, [ctas, duration, property, rate, occupancyRooms, optInBundlesPrice, showModal])

  const taxesAndFeesContent = <Clickable onClick={openPriceDetailsModal}>
    <Caption variant="medium" colour="light-foreground-alt" underline>
      {intl.formatMessage({
        id: 'H29_x-SS95OszUi2OiUM',
        defaultMessage: 'Includes taxes & fees',
        description: 'Label indicating that taxes and fees are included in the price',
      })}
    </Caption>
  </Clickable>

  const noFlightsAvailableMessage = intl.formatMessage(
    {
      id: 'KCe0399qOgmK-1GZSQxq',
      defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
      description: 'Error message when no flights are available from the selected airport',
    },
    { airportName: originAirport?.name },
  )
  const flightsWithin24HoursMessage = intl.formatMessage({
    id: 'WwEhobNYMcqSErYgfeMM',
    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours.',
    description: 'Error message when departure is too soon for online flight booking',
  })
  const flightsAdvanceBookingMessage = intl.formatMessage({
    id: '0j8ptAKnepQILSZ4qfTs',
    defaultMessage: 'Flights can only be booked up to 11 months in advance.',
    description: 'Error message when departure date is too far in advance for flight booking',
  })
  const addFlightsMessage = intl.formatMessage({
    id: 'OrJCJ4isDFtPDzbv6XYB',
    defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
    description: 'Info message about adding flights to the hotel package at checkout',
  })

  /**
   * LPC RATES
   */
  if (rate.service.type === 'lpc') {
    const lpcDiscount = rate.totals.discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rate.totals.discount : 0
    const shouldShowValue = rate.totals.valuedUpToPrice > rate.totals.sellingPrice

    const lpcDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'WlUcNFNb6HRC0YOSv57_',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for LPC property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: '-mSccfdQDlul78PcyBog',
            defaultMessage: '{count, plural, one {# night} other {# nights}}',
            description: 'Duration in nights for LPC property rate option on the form',
          },
          { count: duration },
        )

    return (
      <Group direction="vertical" gap={12}>
        {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4} wrap="wrap" noDisplayWhenEmpty>
          {lpcDiscount > 0 && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={lpcDiscount}
          />}
          {!!rate.paymentScheduleInfo && <PayLaterPill />}
        </Group>}
        {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
        {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
          <div>
            <Caption variant="medium">
              <Group direction="vertical" gap={4}>
                <div>
                  <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                    {lpcDurationLabel}
                  </PriceRowPriceCaption>
                  <LuxPlusPriceGuard
                    showMemberPrice={(rate.luxPlusTotals?.displayPrice ?? 0) > 0}
                    fallbackPrice={<PriceRowPrice
                      data-testid="property-package-price"
                      size="M"
                      price={displayPrice}
                      saleUnit="total"
                    />}
                  >
                    <LuxPlusPriceStack
                      size="M"
                      testid="property-package-price"
                      price={displayPrice}
                      memberPrice={memberDisplayPriceWithBundles}
                      saleUnit="total"
                    />
                  </LuxPlusPriceGuard>
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    size="M"
                    originalValue={rate.totals.valuedUpToPrice + optInBundlesPrice}
                  />}
                  {taxesAndFeesContent}
                </div>
                {rate.exceedsIncludedGuests && <AdditionalGuestsPopup
                  complex={false}
                  description={intl.formatMessage({
                    id: '3KcTGG2Q7zKoXLtwouAC',
                    defaultMessage: '+ Extra guest surcharge',
                    description: 'Description for additional guests popup in property rate option',
                  })}
                  modalContent={rate.includedGuestsExceededPolicy}
                />}
                {shouldShowMargin && <PriceRowMarginText
                  marginValue={rate.totals.marginAud!}
                  margin={rate.totals.margin}
                  type={rate.includesFlights ? 'property_estimate_accom' : 'property_estimate'}
                />}
              </Group>
            </Caption>
          </div>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={rate.includesFlights ? 'estimate' : 'exact'}
          />
        </VerticalSpacer>}
        {rate.includesFlights && (typeof rate.cheapestFlightPrice === 'string' || rate.cheapestFlightPrice === 0) && <VerticalSpacer gap={12}>
          {(rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable') && <BodyText colour="light-foreground-alt" variant="small">
            {noFlightsAvailableMessage}
          </BodyText>}
          {rate.cheapestFlightPrice === 'departureTooEarly' && <BodyText colour="light-foreground-alt" variant="small">
            {flightsWithin24HoursMessage}
          </BodyText>}
          {rate.cheapestFlightPrice === 'departureTooLate' && <BodyText colour="light-foreground-alt" variant="small">
            {flightsAdvanceBookingMessage}
          </BodyText>}
          {rate.cheapestFlightPrice === 0 && <BodyText colour="light-foreground-alt" variant="small">
            {addFlightsMessage}
          </BodyText>}
        </VerticalSpacer>}
        {/*
          * NB: Because we render a "plain" commission rate with a well-defined price, we don't
          * need to worry about aggregating min/max commission rates across all active offers
          * linked to this property.
          */}
        <PriceRowAgentHubCommission
          productType="tactical_ao_hotel"
          offerId={rate.service.offerId}
          packageId={rate.service.packageId}
          offerTotal={rate.totals.sellingPrice}
          kind="plain"
        />
      </Group>
    )
  }

  /**
   * BEDBANK RATES
   */
  if (rate.service.type === 'bedbank') {
    const bedbankDiscount = rawBedbankDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rawBedbankDiscount : 0

    const bedbankDurationLabel = rate.includesFlights
      ? intl.formatMessage(
          {
            id: 'qkFlsC5yUKDoPpbkWOvn',
            defaultMessage: '{count, plural, one {# night} other {# nights}} + flights from',
            description: 'Duration with flights for bedbank property rate option',
          },
          { count: duration },
        )
      : intl.formatMessage(
          {
            id: '00Dl9U8_kbrsX9iSc4kT',
            defaultMessage: '{count, plural, one {# night} other {# nights}} from',
            description: 'Duration in nights for bedbank property rate option',
          },
          { count: duration },
        )

    return (
      <Group direction="vertical" gap={12}>
        {(!rate.includesFlights ||
          (typeof rate.cheapestFlightPrice === 'number' &&
            rate.cheapestFlightPrice > 0)) && (
          <Group direction="horizontal" gap={4} noDisplayWhenEmpty>
            {bedbankDiscount > 0 && (
              <PriceRowValueDiscount
                type="estimate"
                discountPercentage={bedbankDiscount}
              />
            )}
            {!!rate.paymentScheduleInfo && <PayLaterPill />}
          </Group>
        )}

        {rate.includesFlights &&
          rate.cheapestFlightPrice === 'loading' && (
          <LoadingIndicator floating opaque />
        )}

        {(!rate.includesFlights ||
          (typeof rate.cheapestFlightPrice === 'number' &&
            rate.cheapestFlightPrice > 0)) && (
          <VerticalSpacer gap={12}>
            <div>
              <PriceRowPriceCaption startIcon={rate.includesFlights ? <LinePlaneDepartureIcon size="S" /> : undefined}>
                {bedbankDurationLabel}
              </PriceRowPriceCaption>
              <LuxPlusPriceGuard
                showMemberPrice={
                  (rate.luxPlusTotals?.displayPrice ?? 0) > 0
                }
                fallbackPrice={
                  <PriceRowPrice
                    data-testid="property-package-price"
                    size="M"
                    price={displayPrice}
                    saleUnit="total"
                  />
                }
              >
                <LuxPlusPriceStack
                  size="M"
                  testid="property-package-price"
                  price={displayPrice}
                  memberPrice={memberDisplayPriceWithBundles}
                  saleUnit="total"
                />
              </LuxPlusPriceGuard>

              {!!rawBedbankDiscount && (
                <PriceRowValueDiscountWithCaption
                  size="M"
                  originalValue={rate.totals.valuedUpToPrice + optInBundlesPrice}
                />
              )}

              {taxesAndFeesContent}

              {shouldShowMargin && (
                <PriceRowMarginText
                  marginValue={rate.totals.marginAud!}
                  margin={rate.totals.margin}
                  supplier={rate.service.supplier}
                  type={
                    rate.includesFlights
                      ? 'property_estimate_accom'
                      : 'property_estimate'
                  }
                />
              )}
            </div>

            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequests}
              calculationType={
                rate.includesFlights ? 'estimate' : 'exact'
              }
            />
          </VerticalSpacer>
        )}

        {rate.includesFlights &&
          (typeof rate.cheapestFlightPrice === 'string' ||
            rate.cheapestFlightPrice === 0) && (
          <VerticalSpacer gap={12}>
            {(rate.cheapestFlightPrice === 'originSameAsDestination' ||
              rate.cheapestFlightPrice === 'unavailable') && (
              <BodyText colour="light-foreground-alt" variant="small">
                {noFlightsAvailableMessage}
              </BodyText>
            )}

            {rate.cheapestFlightPrice === 'departureTooEarly' && (
              <BodyText colour="light-foreground-alt" variant="small">
                {flightsWithin24HoursMessage}
              </BodyText>
            )}

            {rate.cheapestFlightPrice === 'departureTooLate' && (
              <BodyText colour="light-foreground-alt" variant="small">
                {flightsAdvanceBookingMessage}
              </BodyText>
            )}

            {rate.cheapestFlightPrice === 0 && (
              <BodyText colour="light-foreground-alt" variant="small">
                {addFlightsMessage}
              </BodyText>
            )}
          </VerticalSpacer>
        )}
        {/*
          * NB: Because we render a "plain" commission rate with a well-defined price, we don't
          * need to worry about aggregating min/max commission rates across all active offers
          * linked to this property.
          */}
        <PriceRowAgentHubCommission
          productType="bedbank_hotel"
          offerId={rate.service.propertyId}
          packageId={rate.service.rateId}
          offerTotal={rate.totals.sellingPrice}
          kind="plain"
          checkIn={checkIn}
          checkOut={checkOut}
          rooms={occupancyRooms}
          bedbankPackages={agentHubBedbankPackages}
        />
      </Group>
    )
  }

  return null
}

export default withOfferPageAndReduxState((state, appState) => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  isPropertyUltraLux: getPropertyOfferMapping(state, appState)?.isUltraLux ?? false,
  originAirport: state.airport,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  agentHubBedbankPackages: getAgentHubBedbankPackagesForProperty(state, appState),
  duration: state.duration,
  regionCode: appState.geo.currentRegionCode,
  canShowMargin: appState.auth.account.isSpoofed && appState.system.showMarginInSpoofMode,
  open: !!state.activeFilter,
}), PropertyRatePrice, preventRenderWithOpenFilter)
